import { useEffect, useState } from "react";
import { Attachment } from "../../Attachment";
import * as S from "../../ConvenienceTest/ConvenienceTest.style";
import { DefaultButton } from "@fluentui/react";
import DocumentIntelligenceService from "../../../services/DocumentIntelligenceService";
import { DNIPlaceholder } from "./DNIPlaceHolder";
import UserIdentitiesService, {
  UserIdentityType,
} from "../../../services/UserIdentitiesService";
import { ClaimMessageData } from "../../ConvenienceTest/ConvenienceTestScreenAccountHolderData/ClaimMessage/ClaimMessageType";

interface DocumentUploadProps {
  onUpload: (result: DocumentIntelligenceResult) => void;
  onProcessingDocument: (isProcessing: boolean) => void;
  onResetAccessRequestErrors: () => void;
  shouldLockMainForm: (lock: boolean) => void;
  handleCreateClaimMessage: (claim: ClaimMessageData | null) => void;
  idFront?: string;
  idBack?: string;
  userId: number;
  userIdentity: UserIdentityType;
  isOnlyView: boolean;
  blockedByAuthorizationWarning: boolean;
}

export interface DocumentIntelligenceResult {
  firstName: string;
  lastName: string;
  secondLastName: string;
  documentNumber: string;
  address: string;
  dateOfExpiration: string;
  placeOfBirth: string;
  dateOfBirth: string;
  documentType: string;
  countryRegion: string;
  fileNames: string[];
}

export const DocumentUpload: React.FC<DocumentUploadProps> = ({
  onUpload,
  onProcessingDocument,
  shouldLockMainForm,
  onResetAccessRequestErrors,
  idFront,
  idBack,
  userId,
  userIdentity,
  isOnlyView,
  blockedByAuthorizationWarning,
  handleCreateClaimMessage,
}) => {
  const [documentoAnverso, setDocumentoAnverso] = useState<File | null>(null);
  const [documentoReverso, setDocumentoReverso] = useState<File | null>(null);
  const extractFileName = (input: string): string => {
    if (!input) return "";
    try {
      return new URL(input).pathname.split("/").pop() || "";
    } catch {
      return input;
    }
  };
  const [documentImageFilenames, setDocumentImageFilenames] = useState<
    string[]
  >([extractFileName(idFront!) || "", extractFileName(idBack!) || ""]);
  const [fileFormLocked, setFileFormLocked] = useState<boolean>(false);

  const [documentAnverseError, setDocumentAnverseError] = useState<string>("");
  const [documentReverseError, setDocumentReverseError] = useState<string>("");

  useEffect(() => {
    if (idFront && idBack) {
      setShowDniImage(true);
    }
  }, [idFront, idBack]);

  // spinner dni images
  const [loadingDniImages, setLoadingDniImages] = useState<boolean>(false);
  const [showDniImage, setShowDniImage] = useState<boolean>(false);

  const handleDocument = (event: React.ChangeEvent<HTMLInputElement>) => {
    switch (event.target.id) {
      case "idFront":
        setDocumentoAnverso(event.target.files?.[0] || null);
        setDocumentAnverseError("");
        break;
      case "idBack":
        setDocumentoReverso(event.target.files?.[0] || null);
        setDocumentReverseError("");
        break;
      default:
        break;
    }
  };

  const handleDocumentSubmit = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    userIdentity = { ...userIdentity, idFront: "", idBack: "" }; // Resetear los campos de identidad del usuario
    onResetAccessRequestErrors();

    // reset de errores
    setDocumentAnverseError("");
    setDocumentReverseError("");

    // INFO: bloqueo de campos mientras se procesa el documento
    setFileFormLocked(true);
    shouldLockMainForm(true);

    if (!documentoAnverso) {
      setFileFormLocked(false);
      setDocumentAnverseError("Faltan documentos por subir");
      return;
    }

    if (!documentoReverso) {
      setFileFormLocked(false);
      setDocumentReverseError("Faltan documentos por subir");
      return;
    }

    const documentData = {
      documentoAnverso,
      documentoReverso,
    };

    setLoadingDniImages(true);
    onProcessingDocument(true);

    try {
      const response = await DocumentIntelligenceService.sendDocumentToVerify(
        userId,
        documentData
      );
      if (response && response.documentType === "idDocument.passport") {
        handleCreateClaimMessage({
          id: "passport-warning",
          type: "warning",
          message:
            "El documento subido es un pasaporte. Por favor, asegúrese de que es el documento correcto para este proceso.",
        });

        setDocumentAnverseError(
          "El documento subido es un pasaporte. Por favor, asegúrese de que es el documento correcto para este proceso."
        );
        setShowDniImage(false);
        setLoadingDniImages(false);
        setFileFormLocked(false);
        shouldLockMainForm(true);
        return;
      }

      if (response && response.fileNames && response.fileNames.length > 0) {
        if (response.fileNames.length === 2) {
          userIdentity.idFront = response.fileNames[0];
          userIdentity.idBack = response.fileNames[1];

          const fileNamesOnly = response.fileNames.map(
            (url: string) => url.split("/").pop() ?? ""
          );

          if (userIdentity.id) {
            // es una actualización de un interviniente que ya existe en base de datos
            const updateUserIdentityResponse =
              await UserIdentitiesService.updateUserIdentity(userIdentity);
            if (
              updateUserIdentityResponse.idFront !== null &&
              updateUserIdentityResponse.idBack !== null
            ) {
              setDocumentImageFilenames(fileNamesOnly);
              setShowDniImage(true);
              // documento válido
              onUpload(response as DocumentIntelligenceResult);

              setLoadingDniImages(false);
              shouldLockMainForm(false);

              setFileFormLocked(false);
              setLoadingDniImages(false);
            } else {
              setDocumentAnverseError(
                "Ha ocurrido un error en la subida de los documentos. Inténtelo de nuevo."
              );
              setShowDniImage(false);
              setLoadingDniImages(false);
              setFileFormLocked(false);
              setLoadingDniImages(false);
              shouldLockMainForm(true);
            }
          } else {
            // es un alta nueva
            setDocumentImageFilenames(fileNamesOnly);
            setShowDniImage(true);
            onUpload(response as DocumentIntelligenceResult);

            setLoadingDniImages(false);

            setFileFormLocked(false);
            shouldLockMainForm(false);
          }
        } else {
          // aviso!!!
          setDocumentAnverseError(
            "Ha ocurrido un error en la subida de los documentos. Inténtelo de nuevo."
          );
          setShowDniImage(false);
          setLoadingDniImages(false);
          shouldLockMainForm(true);
          setFileFormLocked(false);
          setLoadingDniImages(false);
        }
      } else {
        if (typeof response === "string") {
          handleCreateClaimMessage({
            id: "error-passport-warning-01",
            type: "warning",
            message: response,
          });
          response.includes("anverso")
            ? setDocumentAnverseError(response)
            : setDocumentReverseError(response);
        } else {
          handleCreateClaimMessage({
            id: "error-passport-warning-02",
            type: "warning",
            message: "No se recibieron nombres de archivo. Inténtelo de nuevo.",
          });
          setDocumentAnverseError(
            "No se recibieron nombres de archivo. Inténtelo de nuevo."
          );
        }
        setShowDniImage(false);
        setLoadingDniImages(false);
        setFileFormLocked(false);
        shouldLockMainForm(true);
      }
    } catch (error: any) {
      // aviso!!!
      console.error("Error al procesar el documento:", error.message || error);
      if (error.message.includes("anverso")) {
        handleCreateClaimMessage({
          id: "error-processing-anverso",
          type: "warning",
          message: error.message || error,
        });
        setDocumentAnverseError(error.message || error);
      } else if (error.message.includes("reverso")) {
        handleCreateClaimMessage({
          id: "error-processing-reverso",
          type: "warning",
          message: error.message || error,
        });
        setDocumentReverseError(error.message || error);
      } else {
        handleCreateClaimMessage({
          id: "upload-error",
          type: "warning",
          message: error.message || error,
        });
        setDocumentAnverseError(
          "Ha ocurrido un error en la subida de los documentos. Inténtelo de nuevo."
        );
      }
    }
  };

  return (
    <>
      <S.FieldSet>
        <S.FieldRBCont>
          <S.Label>DNI / NIE parte delantera *</S.Label>
          <S.FieldFileCont>
            <S.FileInput
              id="idFront"
              name="idFront"
              type="file"
              disabled={
                fileFormLocked || isOnlyView || blockedByAuthorizationWarning
              }
              accept=".png, .jpg, .jpeg, .pdf"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleDocument(event)
              }
            />
          </S.FieldFileCont>
          <p style={{ fontSize: "11px", margin: "4px 0px" }}>
            <span>
              * Se admite los siguientes formatos de imagen: .png, .jpg, .jpeg y
              .pdf
            </span>
          </p>
          <p style={{ fontSize: "13px", fontWeight: "600", color: "red" }}>
            {documentAnverseError}
          </p>
          <S.DocumentIntelligenceWrapper>
            {loadingDniImages ? (
              <DNIPlaceholder addSpinner={true} />
            ) : showDniImage ? (
              <S.DocumentAttachmentWrapper>
                <Attachment
                  fileName={
                    documentImageFilenames && documentImageFilenames[0]
                      ? documentImageFilenames[0]
                      : ""
                  }
                  className="document-preview"
                />
              </S.DocumentAttachmentWrapper>
            ) : (
              <DNIPlaceholder addSpinner={false} />
            )}
          </S.DocumentIntelligenceWrapper>
        </S.FieldRBCont>
        <S.FieldRBCont>
          <S.Label>DNI / NIE parte trasera *</S.Label>
          <S.FieldFileCont>
            <S.FileInput
              id="idBack"
              name="idBack"
              type="file"
              disabled={
                fileFormLocked || isOnlyView || blockedByAuthorizationWarning
              }
              accept=".png, .jpg, .pdf, .jpeg"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleDocument(event)
              }
            />
          </S.FieldFileCont>
          <p style={{ fontSize: "11px", margin: "4px 0px" }}>
            <span>
              * Se admiten los siguientes formatos de imagen: .png, .jpg, .jpeg
              y .pdf
            </span>
          </p>
          <p style={{ fontSize: "13px", fontWeight: "600", color: "red" }}>
            {documentReverseError}
          </p>
          <S.DocumentIntelligenceWrapper>
            {loadingDniImages ? (
              <DNIPlaceholder addSpinner={true} />
            ) : showDniImage ? (
              <S.DocumentAttachmentWrapper>
                <Attachment
                  fileName={
                    documentImageFilenames && documentImageFilenames[1]
                      ? documentImageFilenames[1]
                      : ""
                  }
                  className="document-preview"
                />
              </S.DocumentAttachmentWrapper>
            ) : (
              <DNIPlaceholder addSpinner={false} />
            )}
          </S.DocumentIntelligenceWrapper>
        </S.FieldRBCont>

        <DefaultButton
          className="button tiny-primary-button document-send-button"
          disabled={
            fileFormLocked || isOnlyView || blockedByAuthorizationWarning
          }
          onClick={handleDocumentSubmit}
        >
          Procesar
        </DefaultButton>
      </S.FieldSet>
    </>
  );
};
